'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import * as CustomPropTypes from '../../../constants/PropTypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { FLINT, LORAX } from 'HubStyleTokens/colors';
import H2 from 'UIComponents/elements/headings/H2';
import HR from 'UIComponents/elements/HR';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIModal from 'UIComponents/dialog/UIModal';
import UILink from 'UIComponents/link/UILink';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIImage from 'UIComponents/image/UIImage';
import * as ScopeGroup from 'ecosystem-marketplace-core/constants/scopes/ScopeGroup';
import Scope from 'developer-experience-shared-components/scopes/models/Scope';
import ScopesAuthorization from 'developer-experience-shared-components/scopes/components/scopesAuthorization/ScopesAuthorization';
import { getIsStandardConnection } from '../../../utils/offerings';

const getI18nKey = key => `appMarketplace.detail.overview.sidebar.requirements.permissions.${key}`;

const RequiredPermissions = ({
  appName,
  connectionType,
  homemade,
  iconUrl,
  neededScopeGroups,
  trackInteraction
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const filteredScopeGroups = useMemo(() => {
    return neededScopeGroups && neededScopeGroups.filter(scope => {
      return scope.name !== ScopeGroup.OAUTH;
    });
  }, [neededScopeGroups]);
  const descriptionText = useMemo(() => {
    if (homemade && !getIsStandardConnection(connectionType)) {
      return /*#__PURE__*/_jsx(UITooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('subheadingText.homemadeAppTooltip')
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('subheadingText.homemadeAppPermissions')
        })
      });
    } else if (filteredScopeGroups.isEmpty()) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('subheadingText.noRequiredPermissions'),
        options: {
          appName
        }
      });
    } else {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: getI18nKey('subheadingText.viewPermissions_jsx'),
        elements: {
          Link: UILink
        },
        options: {
          onClick: () => {
            setModalOpen(true);
            trackInteraction('click required account permissions');
          }
        }
      });
    }
  }, [appName, homemade, connectionType, filteredScopeGroups, trackInteraction]);
  const modal = useMemo(() => /*#__PURE__*/_jsxs(UIModal, {
    width: 500,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('modal.header')
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: () => setModalOpen(false)
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      className: "p-bottom-12",
      children: [/*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "center",
        className: "p-y-4",
        children: [/*#__PURE__*/_jsx(UIImage, {
          alt: appName,
          src: iconUrl,
          style: {
            maxHeight: 60
          }
        }), /*#__PURE__*/_jsx(UIIcon, {
          name: "add",
          color: FLINT,
          size: "xs",
          className: "p-x-4"
        }), /*#__PURE__*/_jsx(UIIcon, {
          name: "sprocket",
          color: LORAX,
          size: "sm"
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "text-center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('modal.subtitle'),
          options: {
            appName
          }
        })
      }), /*#__PURE__*/_jsx(HR, {}), /*#__PURE__*/_jsx(ScopesAuthorization, {
        requestedScopes: filteredScopeGroups.toArray()
      })]
    })]
  }), [appName, iconUrl, filteredScopeGroups]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [descriptionText, modalOpen && modal]
  });
};

RequiredPermissions.propTypes = {
  appName: PropTypes.string.isRequired,
  connectionType: CustomPropTypes.connectionType.isRequired,
  homemade: PropTypes.bool.isRequired,
  iconUrl: PropTypes.string.isRequired,
  neededScopeGroups: ImmutablePropTypes.listOf(PropTypes.instanceOf(Scope)).isRequired,
  trackInteraction: PropTypes.func.isRequired
};
export default RequiredPermissions;